import { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, Award, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import CarnetNotesFormateur from '../components/CarnetNotesFormateur';
import CarnetNotesTuteur from '../components/CarnetNotesTuteur';
import EPMSPConsultation from '../components/EPMSPConsultation';
import { moyenneNormalisee } from '../utils/normalizeNote';
import { PodiumStagiaires } from '../components/carnet-general/PodiumStagiaires';

const CarnetsNotes = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [evaluationsTuteur, setEvaluationsTuteur] = useState([]);
  const [heuresParCategorie, setHeuresParCategorie] = useState({});
  const [autonomiesValidees, setAutonomiesValidees] = useState([]);
  const [activeTab, setActiveTab] = useState('formateur');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [user.user_id]);

  const loadData = async () => {
    try {
      const stagiaire_id = user.role === 'stagiaire' ? user.user_id : null;
      
      const [notesRes, evalRes, heuresRes, autoRes] = await Promise.all([
        axios.get(`/notes/stagiaire/${stagiaire_id || user.user_id}`),
        axios.get(`/competences/stagiaire/${stagiaire_id || user.user_id}`),
        axios.get(`/heures-encadrement/stagiaire/${stagiaire_id || user.user_id}`),
        axios.get(`/autonomies-en-attente/stagiaire/${stagiaire_id || user.user_id}`).catch(() => ({ data: [] })),
      ]);
      
      setNotes(notesRes.data || []);
      setEvaluationsTuteur(evalRes.data || []);
      const autoData = Array.isArray(autoRes.data) ? autoRes.data : [];
      setAutonomiesValidees(autoData.filter(a => a.stagiaire_reponse === true));
      
      // Calculer heures par catégorie ET sous-catégorie
      const heuresData = heuresRes.data;
      const heures = Array.isArray(heuresData) ? heuresData : (heuresData?.heures || []);
      
      if (Array.isArray(heures)) {
        const cumul = {};
        heures.forEach(h => {
          const cat = h.categorie || 'Autre';
          const sousCat = h.sous_categorie || 'Général';
          
          if (!cumul[cat]) cumul[cat] = { total: 0, sousCat: {} };
          cumul[cat].total += h.heures || 0;
          
          if (!cumul[cat].sousCat[sousCat]) cumul[cat].sousCat[sousCat] = 0;
          cumul[cat].sousCat[sousCat] += h.heures || 0;
        });
        setHeuresParCategorie(cumul);
      }
      
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const notesArray = Array.isArray(notes) ? notes : [];
  const evalArray = Array.isArray(evaluationsTuteur) ? evaluationsTuteur : [];
  
  const moyenneFormateur = notesArray.length > 0 
    ? moyenneNormalisee(notesArray).toFixed(1) 
    : 0;
  const competencesValidees = evalArray.filter(e => e.statut === 'competent').length;
  const competencesEnCours = evalArray.filter(e => e.statut === 'en_cours').length;
  
  // Calculer le total des heures (nouveau format: { cat: { total: X, sousCat: {...} } })
  const totalHeures = Object.values(heuresParCategorie).reduce((sum, catData) => {
    if (typeof catData === 'number') return sum + catData;
    if (catData && typeof catData === 'object' && catData.total) return sum + catData.total;
    return sum;
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="carnets-notes-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Mes Carnets de Notes</h1>
        <p className="text-slate-600">Consultez vos évaluations du centre de formation et du tuteur</p>
      </div>

      {/* Résumé rapide */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#8DC63F]/30 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#8DC63F]/10 rounded-lg">
              <BookOpen className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#8DC63F]">{moyenneFormateur}/10</p>
              <p className="text-sm text-slate-600">Moyenne Formateur</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{competencesValidees}</p>
              <p className="text-sm text-slate-600">Validées Tuteur</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-orange-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{competencesEnCours}</p>
              <p className="text-sm text-slate-600">En cours Tuteur</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{totalHeures}h</p>
              <p className="text-sm text-slate-600">Encadrement</p>
            </div>
          </div>
        </div>
      </div>

      <PodiumStagiaires />

      {/* Tabs pour les 2 carnets */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('formateur')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 transition-colors ${
              activeTab === 'formateur'
                ? 'bg-[#8DC63F]/10 border-b-2 border-[#8DC63F] text-[#8DC63F]'
                : 'hover:bg-slate-50 text-slate-600'
            }`}
            data-testid="tab-formateur"
          >
            <BookOpen className="h-5 w-5" />
            <span className="font-medium">Carnet Formateur</span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeTab === 'formateur' ? 'bg-[#8DC63F]/20 text-[#8DC63F]' : 'bg-slate-200 text-slate-600'
            }`}>
              {notesArray.length} notes
            </span>
          </button>
          <button
            onClick={() => setActiveTab('tuteur')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 transition-colors ${
              activeTab === 'tuteur'
                ? 'bg-orange-50 border-b-2 border-orange-500 text-orange-600'
                : 'hover:bg-slate-50 text-slate-600'
            }`}
            data-testid="tab-tuteur"
          >
            <Award className="h-5 w-5" />
            <span className="font-medium">Carnet Tuteur</span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeTab === 'tuteur' ? 'bg-orange-100 text-orange-600' : 'bg-slate-200 text-slate-600'
            }`}>
              {evalArray.length} évaluations
            </span>
          </button>
        </div>

        {/* Contenu du tab actif */}
        <div className="p-6">
          {activeTab === 'formateur' ? (
            <CarnetNotesFormateur 
              notes={notesArray} 
              title="Notes du Centre de Formation"
            />
          ) : (
            <CarnetNotesTuteur 
              evaluations={evalArray} 
              heuresParCategorie={heuresParCategorie}
              autonomiesValidees={autonomiesValidees}
              title="Compétences évaluées par le Tuteur"
            />
          )}
        </div>

        {/* Section EPMSP - Consultation */}
        <div className="mt-6">
          <EPMSPConsultation stagiaireId={user.user_id} />
        </div>
      </div>
    </div>
  );
};

export default CarnetsNotes;
